import { Building2, CreditCard, FileText, FolderOpen, Rss, Users, Wallet, type LucideIcon } from 'lucide-react';
import type {
  FinancialYearDetail, FinancialYearSummary, Folder, Meeting, Member, Organization, PaymentPeriod, Post, Proposal,
} from '../types/models';

export interface GuideSnapshot {
  org: Organization;
  members: Member[];
  posts: Post[];
  meetings: Meeting[];
  paymentPeriods: PaymentPeriod[];
  financialYears: FinancialYearSummary[];
  latestYear: FinancialYearDetail | null;
  folders: Folder[];
  proposals: Proposal[];
}

export interface GuideTask {
  id: string;
  label: string;
  // Spotlight highlights the first of these that is visible, e.g. a button behind a tab, then the tab itself.
  targets: string[];
  count: (s: GuideSnapshot) => number;
}

export interface GuideStep {
  id: string;
  route: string;
  icon: LucideIcon;
  title: string;
  description: string;
  tip: string;
  tasks: GuideTask[];
}

export type GuideBaseline = Record<string, number>;

export const guideSteps: GuideStep[] = [
  {
    id: 'settings', route: 'settings', icon: Building2,
    title: 'Fyll i föreningens uppgifter',
    description: 'Organisationsnummer och bankuppgifter skrivs ut på varje faktura som skickas till medlemmarna.',
    tip: 'Uppgifterna kan ändras när som helst under Inställningar.',
    tasks: [
      { id: 'org-number', label: 'Spara föreningens organisationsnummer', targets: ['org-save'], count: s => (s.org.orgNumber ? 1 : 0) },
      { id: 'billing', label: 'Spara bank- och faktureringsuppgifter', targets: ['billing-save'], count: s => (s.org.billingBank && s.org.billingAccount ? 1 : 0) },
    ],
  },
  {
    id: 'members', route: 'members', icon: Users,
    title: 'Lägg till medlemmar',
    description: 'Varje medlem kopplas till sin fastighet och sitt andelstal. Andelstalet styr hur stor del av avgifterna medlemmen betalar.',
    tip: 'Medlemmar som läggs till får en inbjudan att skapa konto. Har ni en lista i Excel kan ni importera alla på en gång.',
    tasks: [
      { id: 'member', label: 'Lägg till en medlem', targets: ['member-add'], count: s => s.members.length - 1 },
    ],
  },
  {
    id: 'feed', route: 'feed', icon: Rss,
    title: 'Informera och kalla till möten',
    description: 'Anslagstavlan når alla medlemmar. Under fliken Möten skickar du kallelser, samlar svar och skriver protokoll.',
    tip: 'Kallelser kan skickas via e-post eller SMS.',
    tasks: [
      { id: 'post', label: 'Publicera ett inlägg', targets: ['post-add', 'posts-tab'], count: s => s.posts.filter(p => p.postType !== 'meeting').length },
      { id: 'meeting', label: 'Skapa ett möte', targets: ['meeting-add', 'meetings-tab'], count: s => s.meetings.length },
    ],
  },
  {
    id: 'payments', route: 'payments', icon: CreditCard,
    title: 'Ta betalt av medlemmarna',
    description: 'En betalningsperiod fördelar avgiften på medlemmarna efter andelstal och skapar en faktura till var och en.',
    tip: 'Markera betalningar som betalda allt eftersom de kommer in.',
    tasks: [
      { id: 'payment-period', label: 'Skapa en betalningsperiod', targets: ['payment-add'], count: s => s.paymentPeriods.length },
    ],
  },
  {
    id: 'ekonomi', route: 'ekonomi', icon: Wallet,
    title: 'Håll ordning på ekonomin',
    description: 'Bokföringsåret samlar intäkter, leverantörsfakturor och andra kostnader, och ger en årsrapport vid bokslutet.',
    tip: 'Medlemsavgifterna från Betalningar räknas in automatiskt.',
    tasks: [
      { id: 'financial-year', label: 'Skapa ett bokföringsår', targets: ['year-add'], count: s => s.financialYears.length },
      {
        id: 'expense', label: 'Registrera en leverantörsfaktura eller kostnad', targets: ['supplier-add', 'cost-add'],
        count: s => (s.latestYear ? s.latestYear.supplierInvoices.length + s.latestYear.otherCosts.length : 0),
      },
    ],
  },
  {
    id: 'documents', route: 'documents', icon: FolderOpen,
    title: 'Samla föreningens dokument',
    description: 'Stadgar, protokoll och avtal samlas i mappar som alla medlemmar når, så inget försvinner när styrelsen byts ut.',
    tip: 'Öppna en mapp för att kunna ladda upp filer i den.',
    tasks: [
      { id: 'folder', label: 'Skapa en mapp', targets: ['folder-add'], count: s => s.folders.length },
      { id: 'document', label: 'Ladda upp ett dokument', targets: ['document-upload', 'folder-open'], count: s => s.folders.reduce((sum, f) => sum + f.documentCount, 0) },
    ],
  },
  {
    id: 'proposals', route: 'proposals', icon: FileText,
    title: 'Ta emot förslag',
    description: 'Medlemmar lämnar förslag som styrelsen godkänner eller avslår, gärna kopplade till ett kommande möte.',
    tip: 'Förslag som kopplas till ett möte syns i mötets dagordning.',
    tasks: [
      { id: 'proposal', label: 'Lämna ett förslag', targets: ['proposal-add'], count: s => s.proposals.length },
    ],
  },
];

export function baselineOf(snapshot: GuideSnapshot): GuideBaseline {
  return Object.fromEntries(guideSteps.flatMap(step => step.tasks.map(task => [task.id, task.count(snapshot)])));
}

export function isTaskDone(task: GuideTask, snapshot: GuideSnapshot, baseline: GuideBaseline = {}): boolean {
  return task.count(snapshot) > (baseline[task.id] ?? 0);
}

export function countDone(snapshot: GuideSnapshot, baseline: GuideBaseline = {}) {
  const tasks = guideSteps.flatMap(step => step.tasks);
  return { done: tasks.filter(task => isTaskDone(task, snapshot, baseline)).length, total: tasks.length };
}
